import { Link } from "react-router-dom";
import { ArrowRight, Flame, Utensils, Music4 } from "lucide-react";
import { SITE } from "@/lib/site";
import PageSeo from "@/components/PageSeo";
import RestaurantSchema from "@/components/RestaurantSchema";

export default function Home() {
  return (
    <>
    <PageSeo
      title="The Barracks Bistro — Lakeside dining in Wellington, Nilgiris"
      description="A multi-cuisine restaurant beside Wellington Lake, inside the Madras Regimental Centre. Book a table, plan a wedding, or join us for a bonfire night."
      path="/"
    />
    <RestaurantSchema />
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[100svh] w-full flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(/images/hero.jpg)` }}
          aria-hidden
        />
        <div className="absolute inset-0 hero-vignette" aria-hidden />
        <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 pb-20 md:pb-28">
          <div className="max-w-2xl">
            <div className="eyebrow !text-[hsl(var(--brass))] rise">Wellington · The Nilgiris</div>
            <h1
              data-testid="hero-title"
              className="rise rise-1 mt-4 text-[hsl(var(--bg))] text-5xl sm:text-6xl lg:text-7xl leading-[0.98]"
            >
              A converted mess hall,
              <br />
              <span className="italic text-[hsl(var(--brass))]">a serious kitchen.</span>
            </h1>
            <p className="rise rise-2 mt-6 text-[hsl(var(--bg))]/85 text-base md:text-lg max-w-xl">
              Long tables, brick walls, the MRC lake at the bottom of the garden. Ingredient-led
              plates, wood-fired evenings, and a drinks list built for the hills.
            </p>
            <div className="rise rise-3 mt-8 flex flex-wrap items-center gap-4">
              <Link to="/reservations" className="btn-brass" data-testid="hero-reserve">
                Reserve a table <ArrowRight size={16} />
              </Link>
              <Link
                to="/menu"
                className="btn-ghost !border-[hsl(var(--bg))]/40 !text-[hsl(var(--bg))] hover:!bg-[hsl(var(--bg))]/10"
                data-testid="hero-menu"
              >
                See the menu
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="relative bg-[hsl(var(--bg))] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { icon: Utensils, title: "The Kitchen", body: "Hill produce, house-cured, wood-fired. A menu that changes with the market." },
              { icon: Flame, title: "The Bonfire", body: "Cool evenings, warm coals. Live sets on Fridays, a lit fire pit every weekend." },
              { icon: Music4, title: "The Room", body: "Colonial bones, brass and brick — for long dinners, birthdays, and quiet Tuesdays." },
            ].map((p) => (
              <div key={p.title} className="border-t border-[hsl(var(--rule))] pt-6" data-testid={`pillar-${p.title.split(' ')[1].toLowerCase()}`}>
                <p.icon size={22} className="text-[hsl(var(--brass))]" />
                <h3 className="mt-4 text-2xl">{p.title}</h3>
                <p className="mt-3 text-[hsl(var(--ink-soft))]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS BAND */}
      <section data-testid="home-events" className="relative py-24 bg-[hsl(var(--bg-2))]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="eyebrow">Events & Private Hire</div>
            <h2 className="mt-3 text-4xl sm:text-5xl">
              Weddings, receptions, <br />
              <span className="italic text-[hsl(var(--brass))]">and the odd bonfire.</span>
            </h2>
            <div className="rule-brass my-6 max-w-xs" />
            <p className="text-[hsl(var(--ink-soft))] max-w-lg">
              The lawn holds two hundred. The veranda holds forty. The kitchen will feed all of them
              properly. We host wedding receptions, corporate off-sites, milestone birthdays, and
              intimate rehearsal dinners — all against the quiet backdrop of the MRC lake.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/events" className="btn-brass" data-testid="events-cta">
                Plan an event <ArrowRight size={16} />
              </Link>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="events-whatsapp">
                WhatsApp us
              </a>
            </div>
          </div>
          <div className="order-1 lg:order-2 relative aspect-[4/5] w-full overflow-hidden">
            <img
              src="/images/events-1.jpg"
              alt="Outdoor wedding setup at The Barracks Bistro"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="events-image"
            />
            <div className="absolute inset-x-6 bottom-6 text-[hsl(var(--bg))]">
              <div className="eyebrow !text-[hsl(var(--brass))]">Lawn ceremony</div>
              <div className="display text-2xl">Under Wellington skies.</div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGA / DINING BAND */}
      <section data-testid="home-siga" className="relative">
        <div
          className="relative w-full min-h-[70svh] bg-cover bg-center flex items-center"
          style={{ backgroundImage: `url(/images/dining.jpg)` }}
        >
          <div className="absolute inset-0 bg-[hsl(var(--ink))]/50" aria-hidden />
          <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 py-24">
            <div className="max-w-xl text-[hsl(var(--bg))]">
              <div className="eyebrow !text-[hsl(var(--brass))]">Part of the SIGA family</div>
              <h2 className="mt-3 text-4xl sm:text-5xl">
                Made with beans, bread <br />
                <span className="italic text-[hsl(var(--brass))]">and a bit of history.</span>
              </h2>
              <div className="rule-brass my-6 max-w-xs" />
              <p className="text-[hsl(var(--bg))]/85">
                We're the dining side of SIGA Coffee — sourcing single-estate beans and hill produce
                from within a day's drive. The bistro turns those raw materials into three meals a
                day, seven days a week.
              </p>
              <div className="mt-8">
                <Link to="/menu" className="btn-brass" data-testid="siga-cta">
                  Explore the menu <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quiet closing */}
      <section className="bg-[hsl(var(--bg))] py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="eyebrow">Reservations</div>
          <h2 className="mt-3 text-3xl sm:text-4xl">Come hungry. Stay for the bonfire.</h2>
          <p className="mt-4 text-[hsl(var(--ink-soft))]">
            We hold tables on weekends, so booking ahead is best. WhatsApp is fastest.
          </p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/reservations" className="btn-brass" data-testid="closing-reserve">
              Reserve a table
            </Link>
            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="closing-whatsapp">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
